import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { ItemCard } from '../ReturnItem';
import s from './ExchangeSummary.module.css';
import { formatCurrencyValue, getCurrencyExponent } from '../../lib/currencyFormatters';
import { Language } from '../../react-components/Language';
import { PaymentFormComponent } from '../PaymentForm';

const formatToDecimal = (moneyObj, signMultiplier = 1) => {
  const result = moneyObj.price_in_cents / getCurrencyExponent(moneyObj.currency);
  return result === 0 ? 0 : result * signMultiplier;
};

const mapStateToProps = ({
  unevenExchanges: { calculatedCart, isPaymentDue } = {},
  returnItems: { returnItems } = {},
  language,
  storeLocator: { selectedLocation: { brand_display_name: brandDisplayName } = {} } = {},
  returnMethods: { selectedMethodKey } = {},
  page: { locale } = {},
}) => ({
  calculatedCart,
  returnItems,
  language,
  methodMessage: brandDisplayName
    ? (language[`returns.step_3.${selectedMethodKey}`] || '').replace('$brand', brandDisplayName)
    : language[`returns.step_3.${selectedMethodKey}`],
  locale,
  isPaymentDue,
});

const ExchangeSummary = ({
  calculatedCart,
  returnItems,
  language,
  methodMessage,
  locale,
  isPaymentDue,
  onStripeInit,
  paymentSubmissionError,
}) => {
  const exchangeItems = returnItems
    .filter((item) => item.exchange_item)
    .map((item) => item.exchange_item);
  const displayAttributes = (returnItems[0]?.item_properties || []).map((property) => property.key);
  const displayDefaultProperties = returnItems[0]?.display_default_properties || false;

  if (!calculatedCart) return null;

  const {
    cart,
    summary,
    payment_client_secret: clientSecret,
    payment_publishable_key: paymentApiKey,
    stripe_account: stripeAccount,
  } = calculatedCart;
  const shippingFee = summary.fees?.find((fee) => fee.fee_type === 'SHIPPING_FEE');
  const signMultiplier = isPaymentDue || summary.total.price_in_cents === 0 ? 1 : -1;

  return (
    <div className={s.container}>
      <Language
        langKey="returns.step_3.uneven_exchange_summary_return_header"
        tagName="h4"
        className={s.header}
      />
      <div>
        {returnItems.map((item, index) => (
          <ItemCard
            type="summary"
            item={{
              ...item,
              exchange_item: undefined,
            }}
            itemQty="return_qty"
            itemIndex={index}
            itemSubtotal={item.return_qty * item.item_price}
          />
        ))}
      </div>

      <Language
        langKey="returns.step_3.uneven_exchange_summary_exchange_header"
        tagName="h4"
        className={s.header}
      />
      {cart.exchange_items.map((item, index) => {
        const { attributes, price, quantity } = exchangeItems.find(
          (i) => i.variant_id === item.new_variant_id,
        );
        const exchangeItem = {
          product_image_url: item.image_src,
          item_name: item.display_name,
          display_default_properties: displayDefaultProperties,
          item_qty: quantity,
          item_price: price,
          item_properties: displayAttributes.map((attribute) => {
            const lowercaseKey = attribute.toLowerCase();
            if (lowercaseKey === 'price') {
              return {
                key: attribute,
                value: formatToDecimal(item.price),
                displayable: true,
              };
            }
            if (lowercaseKey === 'quantity') {
              return {
                key: attribute,
                value: item.quantity,
                displayable: true,
              };
            }
            return {
              key: attribute,
              value: attributes[attribute.toLowerCase()],
              displayable: true,
            };
          }),
        };

        return (
          <ItemCard
            type="summary"
            item={exchangeItem}
            itemQty="item_qty"
            itemIndex={index}
            itemSubtotal={item.quantity * formatToDecimal(item.price)}
          />
        );
      })}

      <div className={s.totals_container}>
        {!!summary.subtotal && (
          <div className={s.row}>
            <p>{language['returns.step_3.subtotal_label']}</p>
            <p>
              {formatCurrencyValue({
                value: formatToDecimal(summary.subtotal, signMultiplier),
                locale,
              })}
            </p>
          </div>
        )}
        {!!shippingFee && (
          <div className={s.row}>
            <p>
              {language['returns.step_3.return_method_label']} {methodMessage}
            </p>
            <p>
              {formatCurrencyValue({
                value: formatToDecimal(shippingFee, signMultiplier),
                locale,
              })}
            </p>
          </div>
        )}
        {!!summary.subtotal_tax && (
          <div className={s.row}>
            <p>{language['returns.step_3.tax_label']}</p>
            <p>
              {formatCurrencyValue({
                value: formatToDecimal(summary.subtotal_tax, signMultiplier),
                locale,
              })}
            </p>
          </div>
        )}
      </div>

      {isPaymentDue ? (
        <div className={s.final_amount}>
          <p>{language['returns.step_3.amount_due_label']}</p>
          <p>
            {formatCurrencyValue({
              value: formatToDecimal(summary.total, signMultiplier),
              locale,
            })}
          </p>
        </div>
      ) : (
        <div className={s.final_amount}>
          <p>{language['returns.step_3.estimated_refund_label']}</p>
          <p>
            {formatCurrencyValue({
              value: formatToDecimal(summary.total, signMultiplier),
              locale,
            })}
          </p>
        </div>
      )}

      {isPaymentDue && (
        <div className={s.payment_container}>
          <Language
            className={s.payment_title}
            tagName="h2"
            langKey="returns.step_3.exchange_payment_form.title"
          />
          <PaymentFormComponent
            onInit={onStripeInit}
            providerKey={paymentApiKey}
            stripeOptions={{
              stripeAccount,
            }}
            elementsOptions={{
              clientSecret,
            }}
            cardNumberLabel={language['returns.step_3.exchange_payment_form.card_number.label']}
            expiryLabel={language['returns.step_3.exchange_payment_form.expiry.label']}
            cvcLabel={language['returns.step_3.exchange_payment_form.cvc.label']}
          />
          {paymentSubmissionError && (
            <div className={s.payment_error} role="alert">
              {paymentSubmissionError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ExchangeSummary.defaultProps = {
  methodMessage: undefined,
  paymentSubmissionError: undefined,
};

ExchangeSummary.propTypes = {
  calculatedCart: PropTypes.object.isRequired,
  returnItems: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  language: PropTypes.object.isRequired,
  methodMessage: PropTypes.string,
  locale: PropTypes.string.isRequired,
  isPaymentDue: PropTypes.bool.isRequired,
  onStripeInit: PropTypes.func.isRequired,
  paymentSubmissionError: PropTypes.string,
};

export default connect(mapStateToProps)(ExchangeSummary);
